import ApiService from '@/services/apiService'
import CacheService from '@/services/cacheService'

import authDtModule from './dataTransferModules/authDt'
import userDtModule from './dataTransferModules/userDt'
import companyDtModule from './dataTransferModules/companyDt'
import presetsDtModule from './dataTransferModules/presetsDt'
import cacheDtModule from './dataTransferModules/cacheDt'
import mapsDtModule from './dataTransferModules/mapsDt'

import dtConfig from '@/config/data-transfer'
import { ModulesMap } from '@/config/data/modulesMaps'
import {
	getUniqKeyForCacheFromPayload,
	createModuleCacheKey,
	transformVersions,
	getCacheVersion,
	defaultGetEntityIdFromUrlParams,
	defaultGetSingleCachedEntity,
	delayPromiseResult,
} from '@/helpers/dataTransferCacheHelper'

const apiService = ApiService()
const cacheService = CacheService()

const { getCacheHashes } = cacheDtModule
function generatePayloadForCacheHashesRequest(sideRequestPayload) {
	const companyId = sideRequestPayload?.urlParams?.companyId

	if (!companyId) {
		return null
	}

	return {
		urlParams: {
			companyId,
		},
	}
}
async function requestCacheHashes(payload) {
	// eslint-disable-next-line no-async-promise-executor
	return new Promise(async (resolve) => {
		const transformedPayload = generatePayloadForCacheHashesRequest(payload)

		if (transformedPayload) {
			const hashes = getCacheHashes(transformedPayload)

			return resolve(hashes)
		}

		return resolve(null)
	})
}

/**
 * getting data from the cache service
 * @param {string} cacheStorage
 * @param {string} currentHashFromBack
 * @param {string} getterFunction
 * @returns {Promise}
 */
function _getDataFromCache(key, currentHashFromBack, cacheStorage) {
	return new Promise((resolve, reject) => {
		cacheService
			.cacheGetRequestWrap(key, currentHashFromBack, cacheStorage)
			.then((res) => {
				resolve(res)
			})
			.catch((err) => {
				reject(err)
			})
	})
}

function _setDataToCache(key, data, version, dataDistributionSchema = null) {
	return new Promise((resolve, reject) => {
		cacheService
			.cacheSetRequestWrap(key, data, version, dataDistributionSchema)
			.then((res) => {
				resolve(res)
			})
			.catch((err) => {
				reject(err)
			})
	})
}

function _transferCachedData(entityConfig, payload, currentCashPayload, apiModule) {
	const keyOfHashedData = currentCashPayload[0]
	const hashOfEntity = currentCashPayload?.[1]

	return new Promise((resolve, reject) => {
		_getDataFromCache(keyOfHashedData, hashOfEntity, entityConfig.cacheStorage)
			.then((cacheResponse) => {
				resolve(cacheResponse)
			})
			.catch(() => {
				// const [, requestPromise] = apiService[entityConfig.apiServiceMethodName](payload)
				const [, requestPromise] =
					apiModule &&
					apiModule[entityConfig.apiServiceMethodName] &&
					typeof apiModule[entityConfig.apiServiceMethodName] === 'function'
						? apiModule[entityConfig.apiServiceMethodName](payload)
						: apiService[entityConfig.apiServiceMethodName](payload)

				requestPromise
					.then((apiRes) => {
						if (entityConfig.isSingleCachedEntity) {
							return resolve(apiRes)
						}

						_setDataToCache(
							keyOfHashedData,
							apiRes,
							hashOfEntity,
							entityConfig.dataDistributionSchema,
						)
							.then(() => {
								_getDataFromCache(keyOfHashedData, hashOfEntity, entityConfig.cacheStorage)
									.then((newListFromCache) => {
										resolve(newListFromCache)
									})
									.catch(() => {
										resolve(apiRes)
									})
							})
							.catch(() => {
								resolve(apiRes)
							})
					})
					.catch((apiErr) => {
						reject(apiErr)
					})
			})
	})
}

export function dataTransferWrap(moduleName, entityConfig, payload, apiModule) {
	const saveVersions = (versionsFromServer, uniqKey) => {
		const transformedVersions = transformVersions(versionsFromServer)
		cacheService.updateCurrentVersions(uniqKey, transformedVersions)
	}
	const requestFromServer = () => {
		return apiModule &&
			apiModule[entityConfig.apiServiceMethodName] &&
			typeof apiModule[entityConfig.apiServiceMethodName] === 'function'
			? apiModule[entityConfig.apiServiceMethodName](payload)
			: apiService[entityConfig.apiServiceMethodName](payload)
	}
	const uniqKey = getUniqKeyForCacheFromPayload(payload)

	// eslint-disable-next-line no-async-promise-executor
	return new Promise(async (resolve, reject) => {
		if (moduleName === ModulesMap.CACHE && uniqKey) {
			// const currentVersions = await requestFromServer() // in this context this method requests versions
			const [, currentVersionsPromise] = requestFromServer() // in this context this method requests versions
			const currentVersions = await currentVersionsPromise

			saveVersions(currentVersions, uniqKey)

			return resolve(currentVersions)
		}

		if (!entityConfig.useCache || !uniqKey) {
			const [requestConfig, requestPromise] = requestFromServer()

			/* при логине или регистрации отправляется POST запрос но никаких версий быть не может */
			if (
				['POST', 'PUT', 'PATCH', 'DELETE'].includes(requestConfig?.requestOptions?.method) &&
				!requestConfig?.url.includes('/auth/') &&
				!requestConfig?.url.includes('/registration/')
			) {
				const getWrappedPromise = await delayPromiseResult(requestPromise)
				const currentVersions = await requestCacheHashes(payload)

				saveVersions(currentVersions, uniqKey)

				return getWrappedPromise()
					.then((res) => resolve(res))
					.catch((err) => reject(err))
			}

			return requestPromise.then((res) => resolve(res)).catch((err) => reject(err))
		}

		const cacheKey = createModuleCacheKey(moduleName, uniqKey)
		const version = await getCacheVersion(uniqKey, cacheKey, cacheService)

		if (entityConfig.isSingleCachedEntity) {
			const dataFromCache = await _transferCachedData(
				entityConfig,
				payload,
				[cacheKey, version],
				apiModule,
			)

			if (!dataFromCache) {
				return requestFromServer()
					.at(1)
					.then((res) => resolve(res))
					.catch((err) => reject(err))
			}

			const singleEntity = (entityConfig.findSingleEntity || defaultGetSingleCachedEntity)(
				dataFromCache,
				(entityConfig.getEntityIdFromPayload || defaultGetEntityIdFromUrlParams)(payload),
			)

			if (singleEntity) {
				return resolve(singleEntity)
			} else {
				return requestFromServer()
					.at(1)
					.then((res) => resolve(res))
					.catch((err) => reject(err))
			}
		} else if (entityConfig.useCache) {
			const dataFromCache = await _transferCachedData(
				entityConfig,
				payload,
				[cacheKey, version],
				apiModule,
			)

			if (dataFromCache) {
				return resolve(dataFromCache)
			} else {
				return requestFromServer()
					.at(1)
					.then((res) => resolve(res))
					.catch((err) => reject(err))
			}
		}

		return _transferCachedData(entityConfig, payload, [cacheKey, version], apiModule)
			.then((res) => resolve(res))
			.catch((err) => reject(err))
	})
}

/*
	создал отдельный метод который будет использоваться в абстракции DataTransferModule.
	проблема в том, что в метод dataTransferWrap в старых дт-модулях передавались такие артефакты как cashedPayload
	а мне нужно в этот метод передать 4 параметром апи-модуль что бы не было необходимости его регистрировать src/services/apiService.js
*/
export function dataTransferWrapWithIncludedApiModule(
	moduleName,
	apiModule,
	entityConfig,
	payload,
) {
	// debugger
	return dataTransferWrap(moduleName, entityConfig, payload, apiModule)
}

function getUserData() {
	return dataTransferWrap(dtConfig[ModulesMap.USER].currentUser)
}

export default function () {
	return {
		cache: cacheDtModule,
		presets: presetsDtModule,
		auth: authDtModule,
		user: userDtModule,
		company: companyDtModule,
		maps: mapsDtModule,
		getUserData,
	}
}
